import React, { useContext, useMemo, useState } from 'react';
import { History } from 'history';
import { v4 as uuidv4 } from 'uuid';
import { HyruleToken } from '@nintendo/hyrule-react-commons';
import Utils from '../../shared/Utils';
import { NintendoAccountApi } from '../../api/NintendoAccountApi';
import HyruleTokenContext from './HyruleTokenContext';
import { NintendoAccountResponse, PartOrderApiModel } from '../../api/models';
import { PartOrderApi } from '../../api';

interface performSearchInterface {
    (text: string, history: History): void;
}

interface clearSearchInterface {
    (history: History): void;
}

export interface SearchContextInterface {
    searchText: string;
    resolvedNaid: string;
    resolvedEmail: string;
    resolvedSerialNumber: string;
    resolvedOrderNumber: string;
    correlationId: string;
    searchFormatError: string;
    isNintendoAccountLoading: boolean;
    isNintendoAccountError: boolean;
    isNintendoAccountNotFound: boolean;
    isGuestOrder: boolean;
    performSearch: performSearchInterface;
    clearSearch: clearSearchInterface;
    resetResolvedState: () => void;
}

interface SearchStateInterface {
    naid: string | null;
    email: string | null;
    serialNumber: string | null;
    orderNumber?: string;
}

export const SearchContext = React.createContext<SearchContextInterface | null>(null);

export const SearchContextProvider: React.FC<React.ReactNode> = ({ children }) => {
    const [searchText, setSearchText] = useState<string>('');
    const [naid, setNaid] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [serialNumber, setSerialNumber] = useState<string>('');
    const [orderNumber, setOrderNumber] = useState<string>('');
    const [correlationId, setCorrelationId] = useState<string>('');
    const [searchFormatError, setSearchFormatError] = useState<string>('');
    const [isNintendoAccountLoading, setIsNintendoAccountLoading] = useState(false);
    const [isNintendoAccountNotFound, setIsNintendoAccountNotFound] = useState(false);
    const [isNintendoAccountError, setIsNintendoAccountError] = useState(false);
    const [isGuestOrder, setIsGuestOrder] = useState(false);
    const token: HyruleToken = useContext(HyruleTokenContext);

    const performSearch = (text: string, history: History): void => {
        const newlyCreatedCorrelationIdForThisSearch = uuidv4();
        setCorrelationId(newlyCreatedCorrelationIdForThisSearch);
        setSearchText(text);

        const isValidEmail: boolean = Utils.isValidEmail(text);
        const isValidSerialNumber: boolean = Utils.isValidSerialNumber(text);
        const isValidNAID: boolean = Utils.isValidNAID(text);
        const isValidOrderNumber = Utils.isValidOrderNumber(text);

        if (isValidEmail || isValidNAID) {
            setSearchFormatError('');
            resolveNaidOrEmail();
        } else if (isValidSerialNumber) {
            setSearchFormatError('');
            resolveSerialNumber();
        } else if (isValidOrderNumber) {
            setSearchFormatError('');
            resolveOrderNumber();
        } else {
            setSearchFormatError(
                'Your search term is not in a format that is recognized. Please check your search term and try again.',
            );
        }

        function resolveNaidOrEmail(): void {
            const email = isValidEmail ? text : '';
            const naid = isValidNAID ? text : '';

            setEmail(email);
            setNaid(naid);
            setSerialNumber('');
            setOrderNumber('');
            setIsGuestOrder(false);

            pushToHistory({ naid, email, serialNumber: '' });

            requestNaidAndEmailFromNintendoAccountApi(email, naid);
        }

        function requestNaidAndEmailFromNintendoAccountApi(email: string, naid: string): void {
            setIsNintendoAccountLoading(true);
            setIsNintendoAccountError(false);
            setIsNintendoAccountNotFound(false);

            NintendoAccountApi.getNaidOrEmail(
                { email, naid },
                token,
                newlyCreatedCorrelationIdForThisSearch,
            )
                .then((nasResponse: NintendoAccountResponse) => {
                    if (token.accessToken && !nasResponse.nasError && !nasResponse.nasNotFound) {
                        setNaid(nasResponse.naid || '');
                        setEmail(nasResponse.email || '');
                    }
                    if (nasResponse.nasNotFound) {
                        setIsNintendoAccountNotFound(true);
                    }
                    if (nasResponse.nasError) {
                        setIsNintendoAccountError(true);
                    }
                })
                .finally(() => {
                    setIsNintendoAccountLoading(false);
                });
        }

        function resolveSerialNumber(): void {
            setSerialNumber(text);
            setEmail('');
            setNaid('');
            setOrderNumber('');
            setIsGuestOrder(false);

            pushToHistory({ naid: null, email: null, serialNumber: text });
        }

        function resolveOrderNumber(): void {
            setIsNintendoAccountLoading(true);
            setSerialNumber('');
            setEmail('');
            setNaid('');
            setOrderNumber(text);
            setIsGuestOrder(false);

            // TODO: Call a new method to resolve order number to NAID. If order doesn't exist, set error/messaging.
            PartOrderApi.getPartOrders({ orderNumber: text }, token, correlationId)
                .then((partOrderResponse: PartOrderApiModel) => {
                    const associatedNaid = partOrderResponse?.items[0]?.naid;
                    const hasRealNaid = Utils.isValidNAID(associatedNaid || '');

                    if (token.accessToken && hasRealNaid) {
                        setNaid(associatedNaid);
                        requestNaidAndEmailFromNintendoAccountApi('', associatedNaid);
                        pushToHistory({
                            naid: associatedNaid,
                            email: null,
                            serialNumber: null,
                            orderNumber: text,
                        });
                    } else if (token.accessToken && !hasRealNaid) {
                        setIsGuestOrder(true);
                        setIsNintendoAccountLoading(false);
                        pushToHistory({
                            naid: null,
                            email: null,
                            serialNumber: null,
                            orderNumber: text,
                        });
                    }
                })
                .catch(() => {
                    setIsNintendoAccountNotFound(true);
                })
                .finally(() => {
                    setIsNintendoAccountLoading(false);
                });
        }

        function pushToHistory(searchState: SearchStateInterface): void {
            const id =
                searchState.naid ||
                searchState.email ||
                searchState.serialNumber ||
                searchState.orderNumber;

            if (!id) {
                throw new Error('Invalid search state object!');
            }

            const path = `/?search=${encodeURIComponent(id)}`;
            const currentPath = `${history.location.pathname}${history.location.search}`;
            if (currentPath !== path) history.push(path, searchState);
        }
    };

    const clearSearch = (history: History): void => {
        setSearchText('');
        setNaid('');
        setEmail('');
        setSerialNumber('');
        setOrderNumber('');
        resetNintendoAccountState();
        history.push('/');
        setIsGuestOrder(false);
    };

    function resetNintendoAccountState(): void {
        setIsNintendoAccountLoading(false);
        setIsNintendoAccountError(false);
        setIsNintendoAccountNotFound(false);
        setIsGuestOrder(false);
    }

    const resetResolvedState = (): void => {
        setNaid('');
        setEmail('');
        setSerialNumber('');
        setOrderNumber('');
        resetNintendoAccountState();
    };

    const contextValue = useMemo<SearchContextInterface>(
        () => ({
            searchText,
            resolvedNaid: naid,
            resolvedEmail: email,
            resolvedSerialNumber: serialNumber,
            resolvedOrderNumber: orderNumber,
            searchFormatError,
            isNintendoAccountError,
            isNintendoAccountLoading,
            isNintendoAccountNotFound,
            isGuestOrder,
            correlationId,
            performSearch,
            clearSearch,
            resetResolvedState,
        }),
        [
            searchText,
            naid,
            email,
            serialNumber,
            orderNumber,
            searchFormatError,
            isNintendoAccountError,
            isNintendoAccountLoading,
            isNintendoAccountNotFound,
            isGuestOrder,
            performSearch,
            clearSearch,
        ],
    );

    return <SearchContext.Provider value={contextValue}>{children}</SearchContext.Provider>;
};
